import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { BookOpen, ExternalLink, Store, Heart, Instagram } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

interface StaffFavorite {
  id: string;
  staff_name: string;
  book_title: string;
  book_author: string;
  genre: string;
  why_i_love_it: string;
  cover_url: string | null;
  purchase_url: string | null;
  store_name: string;
  city: string;
  state: string;
}

interface Influencer {
  id: string;
  name: string;
  platform: string;
  handle: string;
  profile_url: string | null;
  genres: string[];
  bio: string;
}

export default function StaffFavorites() {
  const [favorites, setFavorites] = useState<StaffFavorite[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"picks" | "creators">("picks");

  useEffect(() => {
    async function load() {
      const [favsRes, infRes] = await Promise.all([
        supabase.rpc("get_staff_favorites_with_bookstore" as any),
        supabase.rpc("get_approved_influencers" as any),
      ]);

      if (!favsRes.error && favsRes.data) {
        setFavorites(favsRes.data as StaffFavorite[]);
      }
      if (!infRes.error && infRes.data) {
        setInfluencers(infRes.data as Influencer[]);
      }
      setLoading(false);
    }
    load();
  }, []);

  const hasContent = favorites.length > 0 || influencers.length > 0;

  const profileUrl = (inf: Influencer) => {
    if (inf.profile_url) return inf.profile_url;
    if (inf.platform === "tiktok") return `https://www.tiktok.com/@${inf.handle.replace(/^@/, "")}`;
    return `https://www.instagram.com/${inf.handle.replace(/^@/, "")}`;
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-4xl px-4 py-12">
        {/* Hero */}
        <div className="mb-10 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-accent/20">
            <Heart className="h-8 w-8 text-accent-foreground" />
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
            Between Us
          </h1>
          <p className="mx-auto mt-3 max-w-lg text-muted-foreground">
            Who we're actually texting when we finish a book at 2am.
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-8 flex justify-center gap-2">
          <button
            onClick={() => setActiveTab("picks")}
            className={`rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors ${
              activeTab === "picks"
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground"
            }`}
          >
            <Store className="mr-1.5 inline h-4 w-4" />
            Bookstore Staff Picks
          </button>
          <button
            onClick={() => setActiveTab("creators")}
            className={`rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors ${
              activeTab === "creators"
                ? "bg-secondary text-secondary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground"
            }`}
          >
            <Instagram className="mr-1.5 inline h-4 w-4" />
            Book Creators
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : activeTab === "picks" ? (
          /* ========== BOOKSTORE STAFF PICKS ========== */
          favorites.length === 0 ? (
            <EmptyState
              icon={<BookOpen className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />}
              title="The Wall Is Being Built"
              description="We're reviewing bookstore partnerships right now. Once our first partners are approved, their staff picks will fill this wall with handpicked gems."
              actions={
                <>
                  <Link to="/partner/bookstore">
                    <Button variant="default" className="rounded-xl">
                      <Store className="mr-2 h-4 w-4" /> Become a Partner Bookstore
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline" className="rounded-xl">
                      Get Recs from Our AI
                    </Button>
                  </Link>
                </>
              }
            />
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {favorites.map((fav) => (
                <article
                  key={fav.id}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-border/50 bg-card transition-shadow hover:shadow-lg"
                >
                  {fav.cover_url && (
                    <div className="aspect-[3/4] w-full overflow-hidden bg-muted">
                      <img
                        src={fav.cover_url}
                        alt={`Cover of ${fav.book_title}`}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-5">
                    <span className="mb-2 inline-block w-fit rounded-full bg-primary/10 px-3 py-0.5 text-xs font-semibold text-primary">
                      {fav.genre}
                    </span>
                    <h3 className="font-display text-lg font-bold leading-tight text-foreground">
                      {fav.book_title}
                    </h3>
                    <p className="mt-0.5 text-sm text-muted-foreground">by {fav.book_author}</p>
                    <blockquote className="mt-3 flex-1 border-l-2 border-primary/30 pl-3 text-sm italic text-muted-foreground">
                      "{fav.why_i_love_it}"
                    </blockquote>
                    <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary/10 text-secondary font-bold text-xs">
                        {fav.staff_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <span className="font-semibold text-foreground">{fav.staff_name}</span>
                        {fav.store_name && (
                          <span className="block text-xs">
                            {fav.store_name} · {fav.city}, {fav.state}
                          </span>
                        )}
                      </div>
                    </div>
                    {fav.purchase_url && (
                      <a
                        href={fav.purchase_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                      >
                        Get this book <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )
        ) : (
          /* ========== BOOK CREATORS ========== */
          influencers.length === 0 ? (
            <EmptyState
              icon={<Instagram className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />}
              title="Creators Coming Soon"
              description="We're reviewing influencer partnerships right now. Once approved, BookTok and Bookstagram creators will appear here with their genre specialties."
              actions={
                <>
                  <Link to="/partner/influencer">
                    <Button variant="default" className="rounded-xl">
                      <Instagram className="mr-2 h-4 w-4" /> Apply as a Creator
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline" className="rounded-xl">
                      Get Recs from Our AI
                    </Button>
                  </Link>
                </>
              }
            />
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {influencers.map((inf) => (
                <article
                  key={inf.id}
                  className="group flex flex-col rounded-2xl border border-border/50 bg-card p-5 transition-shadow hover:shadow-lg"
                >
                  {/* Avatar & platform badge */}
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary/10 text-secondary font-display text-lg font-bold">
                      {inf.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display text-base font-bold text-foreground">{inf.name}</h3>
                      <span className="text-xs text-muted-foreground">
                        {inf.platform === "both" ? "Instagram & TikTok" : inf.platform === "instagram" ? "Instagram" : "TikTok"}
                      </span>
                    </div>
                  </div>

                  {/* Bio */}
                  <p className="mb-4 flex-1 text-sm text-muted-foreground line-clamp-4">
                    {inf.bio}
                  </p>

                  {/* Genre tags */}
                  <div className="mb-4 flex flex-wrap gap-1.5">
                    {inf.genres.slice(0, 4).map((genre) => (
                      <Badge key={genre} variant="outline" className="rounded-full px-2.5 py-0.5 text-xs">
                        {genre}
                      </Badge>
                    ))}
                    {inf.genres.length > 4 && (
                      <Badge variant="outline" className="rounded-full px-2.5 py-0.5 text-xs">
                        +{inf.genres.length - 4}
                      </Badge>
                    )}
                  </div>

                  {/* Follow link */}
                  <a
                    href={profileUrl(inf)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-xl bg-secondary/10 px-4 py-2 text-sm font-semibold text-secondary transition-colors hover:bg-secondary/20"
                  >
                    <Instagram className="h-4 w-4" />
                    Follow {inf.handle.startsWith("@") ? inf.handle : `@${inf.handle}`}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </article>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

/* ========== Reusable empty state ========== */
function EmptyState({
  icon,
  title,
  description,
  actions,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  actions: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border/50 bg-card p-12 text-center">
      {icon}
      <h2 className="mb-2 font-display text-xl font-bold text-foreground">{title}</h2>
      <p className="mx-auto mb-6 max-w-md text-muted-foreground">{description}</p>
      <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        {actions}
      </div>
    </div>
  );
}
